import React, { useEffect, useRef } from 'react';
import { CloudinaryImage } from '../../types/Gallery';
import { extractMetadata, CloudinaryUploadResult } from '../../utils/MetadataExtractor';

const { useState } = React;

interface UploadWidgetProps {
  onUpload: (result: any) => void;
  className?: string;
  buttonText?: string;
  folder?: string;
}

const UploadWidget: React.FC<UploadWidgetProps> = ({ 
  onUpload, 
  className = "upload-widget",
  buttonText = "Upload Images",
  folder = "carmen_lorraine_photos/galleries"
}) => {
  const widgetRef = useRef<any>(null);
  const uploadedImagesRef = useRef<any[]>([]);
  const [cloudinary, setCloudinary] = useState<any>(null);
  const [uploadInProgress, setUploadInProgress] = useState(false);

  useEffect(() => {
    // Dynamically load Cloudinary widget script
    const script = document.createElement('script');
    script.src = 'https://upload-widget.cloudinary.com/global/all.js';
    script.async = true;
    script.onload = () => {
      console.log('Cloudinary script loaded');
      setCloudinary(window.cloudinary);
    };
    script.onerror = () => {
      console.error('Failed to load Cloudinary script');
    };
    document.head.appendChild(script);

    return () => {
      // Cleanup
      if (widgetRef.current?.close) {
        widgetRef.current.close();
      }
    };
  }, []);

  const openWidget = () => {
    console.log('Upload button clicked');
    console.log('Cloudinary available:', !!cloudinary);
    
    if (!cloudinary) {
      console.error('Cloudinary not loaded yet');
      alert('Cloudinary upload widget is still loading. Please wait a moment and try again.');
      return;
    }

    console.log('Creating upload widget...');
    
    // Try different upload presets in order of preference
    const uploadPresets = ['unsigned_upload', 'carmen_lorraine_photos', 'ml_default'];
    const selectedPreset = uploadPresets[0]; // Start with unsigned_upload
    
    console.log('Using upload preset:', selectedPreset);
    
    const widget = cloudinary.createUploadWidget(
      {
        cloudName: process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'di1mzmlts',
        uploadPreset: selectedPreset,
        sources: ['local', 'url', 'camera'],
        cropping: false,
        multiple: true,
        maxImageWidth: 4000,
        maxImageHeight: 4000,
        folder: folder,
        resourceType: "auto",
        clientAllowedFormats: ["jpg", "jpeg", "png", "gif", "webp"],
        maxFiles: 50,
        singleUploadMaxLength: 10000000, // 10MB
        theme: 'minimal',
        // Enhanced metadata capture
        context: {
          caption: '',
          alt: '',
          photographer: 'Carmen Lorraine',
          copyright: '© Carmen Lorraine Photography'
        },
        // Enable EXIF data extraction
        exif: true,
        // Enable GPS data (if available in images)
        gps: true,
        // Enable image analysis
        image_metadata: true,
        // Enable automatic tagging
        auto_tagging: 0.6,
        styles: {
          palette: {
            window: '#FFFFFF',
            sourceBg: '#F4F4F5',
            windowBorder: '#90A0B3',
            tabIcon: '#000000',
            inactiveTabIcon: '#555A5F',
            menuIcons: '#555A5F',
            link: '#0433FF',
            action: '#339933',
            inProgress: '#0433FF',
            complete: '#20B832',
            error: '#EA2727',
            textDark: '#000000',
            textLight: '#FFFFFF'
          }
        }
      },
      (error: any, result: any) => {
        console.log('🔍 UploadWidget callback triggered:', { error, result });
        
        if (error) {
          console.error('Upload failed:', error);
          
          // If it's a preset error, show helpful message
          if (error.status === 'Upload preset not found') {
            alert('Upload preset not found. Please create an "unsigned_upload" preset in your Cloudinary dashboard, or contact support.');
          } else {
            alert('Upload failed: ' + (error.message || JSON.stringify(error)));
          }
          
          setUploadInProgress(false);
          uploadedImagesRef.current = [];
          return;
        }
        
        if (result.event === "upload-added") {
          console.log('📸 Upload added event:', result);
          setUploadInProgress(true);
        }
        
        if (result.event === "success") {
          console.log('📸 Upload success, extracting metadata:', result.info);
          // Extract enhanced metadata from upload result
          const enhancedImage = extractMetadata(result.info as CloudinaryUploadResult);
          uploadedImagesRef.current.push(enhancedImage);
          console.log('📸 Enhanced metadata:', enhancedImage);
        }
        
        if (result.event === "close") {
          console.log('📸 Upload widget closed, uploaded images:', uploadedImagesRef.current);
          if (uploadedImagesRef.current.length > 0) {
            console.log('📸 Calling onUpload with images:', uploadedImagesRef.current);
            // Ensure we're passing the array correctly
            const imagesToUpload = [...uploadedImagesRef.current];
            console.log('📸 Images array to upload:', imagesToUpload);
            console.log('📸 First image structure:', imagesToUpload[0]);
            onUpload(imagesToUpload); // Send a copy of the array
          } else {
            console.log('📸 No images uploaded, not calling onUpload');
          }
          
          setUploadInProgress(false);
          uploadedImagesRef.current = []; // Reset the ref
        }
        
        // Handle batch upload completion
        if (result.event === "batch-cancelled" || result.event === "batch-received") {
          console.log('📸 Batch event:', result.event);
          if (result.info && result.info.files && result.info.files.length > 0) {
            console.log('📸 Batch files received:', result.info.files);
          }
        }
        
        // Log any other events
        if (result.event && !['upload-added', 'success', 'close'].includes(result.event)) {
          console.log('📸 Other upload event:', result.event, result);
        }
      }
    );

    widgetRef.current = widget;
    widget.open();
  };

  return (
    <button
      className={className}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        console.log('🔘 Upload button clicked, cloudinary:', !!cloudinary);
        openWidget();
      }}
      type="button"
      disabled={!cloudinary || uploadInProgress}
      style={{ pointerEvents: (!cloudinary || uploadInProgress) ? 'none' : 'auto' }}
    >
      {!cloudinary ? 'Loading...' : uploadInProgress ? 'Uploading...' : buttonText}
    </button>
  );
};

// Declare window.cloudinary for TypeScript
declare global {
  interface Window {
    cloudinary: any;
  }
}

export default UploadWidget;
